import { REMEDIATION_IMPACT_API } from "../../vars";

// Long-running Ollama requests can outlive the public connector timeout.
// Start a backend job first, then poll for completion from the UI.
const MAX_RETRIES = 3;
const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 180;

function buildHeaders() {
  const token = sessionStorage.getItem("access_token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function buildFriendlyError(status, detail) {
  if (status === 521) {
    return new Error(
      "Impact analysis service is temporarily unavailable. The upstream connector returned 521 (application offline). Please retry in a moment."
    );
  }
  if (detail) return new Error(detail);
  return new Error("Impact analysis failed");
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function buildPayload({ device, rule, currentUser, realTime, resultId }) {
  return {
    deviceName: device?.hostName || "",
    msbName: device?.msbName || device?.baselineName || device?.complianceBaseline || "",
    ruleName: rule?.name || rule?.rule || rule?.ruleName || "",
    remediationScript: rule?.remediationScript || "",
    deviceType: device?.deviceType?.name || device?.deviceType || device?.type || "",
    deviceId: device?.id ?? null,
    ruleId: rule?.id ?? null,
    msbId: device?.msbId ?? null,
    resultId: resultId ?? null,
    currentUser: currentUser || null,
    realTime: Number(realTime) || 0,
    severity: rule?.severity || null,
    ruleDescription: rule?.remediationDesc || rule?.description || null,
    deviceCriticality: device?.criticality || device?.deviceCriticality || null,
    previousOutput: rule?.previousOutput || null,
    previousValid: rule?.previousValid ?? null,
    deviceConfig: rule?.deviceConfig || null,
  };
}

async function fetchAssessmentJobStart(payload, attempt = 1) {
  const response = await fetch(`${REMEDIATION_IMPACT_API}/assessment/jobs`, {
    method: "POST",
    headers: buildHeaders(),
    body: JSON.stringify(payload),
  });

  if (response.status === 521 && attempt < MAX_RETRIES) {
    console.warn(`[impact-analysis] 521 on attempt ${attempt}, retrying...`);
    return fetchAssessmentJobStart(payload, attempt + 1);
  }

  if (!response.ok) {
    let detail = "";
    try { detail = await response.text(); } catch (_) { /* ignore */ }
    throw buildFriendlyError(response.status, detail);
  }

  return response.json();
}

async function fetchAssessmentJobStatus(jobId) {
  const response = await fetch(`${REMEDIATION_IMPACT_API}/assessment/jobs/${jobId}`, {
    method: "GET",
    headers: buildHeaders(),
  });

  if (!response.ok) {
    let detail = "";
    try { detail = await response.text(); } catch (_) { /* ignore */ }
    throw buildFriendlyError(response.status, detail);
  }

  return response.json();
}

async function fetchAssessment(payload) {
  const start = await fetchAssessmentJobStart(payload);
  const jobId = start?.job_id;

  if (!jobId) {
    throw new Error("Impact analysis job could not be started");
  }

  for (let poll = 0; poll < MAX_POLLS; poll += 1) {
    const status = await fetchAssessmentJobStatus(jobId);

    if (status?.status === "completed") {
      return status.result;
    }

    if (status?.status === "failed") {
      throw new Error(status.error || "Impact analysis failed");
    }

    await sleep(POLL_INTERVAL_MS);
  }

  throw new Error("Impact analysis is taking longer than expected. Please retry in a moment.");
}

export async function assessImpact({ device, rule, currentUser, realTime, resultId }) {
  console.log("[impact-analysis] service start");

  const started = performance.now();
  console.time("impact-analysis-request");

  const payload = buildPayload({ device, rule, currentUser, realTime, resultId });

  try {
    const data = await fetchAssessment(payload);
    const elapsed = Math.round(performance.now() - started);
    console.log("[impact-analysis] fetching", `${REMEDIATION_IMPACT_API}/assessment/jobs`);
    console.log("[impact-analysis] success elapsed_ms:", elapsed);
    return data;
  } catch (error) {
    const elapsed = Math.round(performance.now() - started);
    console.error("[impact-analysis] failed elapsed_ms:", elapsed, error);
    throw error;
  } finally {
    console.timeEnd("impact-analysis-request");
  }
}
